/**
 * Role-based access control matrix and helpers.
 *
 * Single source of truth — when the matrix changes here, the sidebar gating,
 * route protection, AccountSettings sections, and the in-app
 * RolePermissionsModal all reflect it automatically.
 *
 * Role values match `user_profiles.role` (see TEAM_ROLES in src/constants/index.js).
 */

export const MODULES = Object.freeze({
  BOOKING: 'booking',
  TRACKING: 'tracking',
  CLIENTS: 'clients',
  FINANCIALS: 'financials',
  ADMIN: 'admin',
});

export const ACTIONS = Object.freeze({
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  SEND_QUOTE: 'sendQuote',
  GENERATE_INVOICE: 'generateInvoice',
});

export const SCOPES = Object.freeze({
  NONE: 'none',
  SELF: 'self',
  GLOBAL: 'global',
});

export const ROLES = Object.freeze({
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  BROKER: 'broker',
  JUNIOR_BROKER: 'junior_broker',
  FINANCE: 'finance',
  FLIGHT_COORDINATOR: 'flight_coordinator',
  COMPLIANCE_QA: 'compliance_qa',
});

const deny = { read: false, create: false, update: false, delete: false, scope: SCOPES.NONE, label: 'None' };

const full = (scope, label, extras = {}) => ({
  read: true,
  create: true,
  update: true,
  delete: true,
  scope,
  label,
  ...extras,
});

const readOnly = (scope, label, extras = {}) => ({
  read: true,
  create: false,
  update: false,
  delete: false,
  scope,
  label,
  ...extras,
});

const crud = (scope, label, extras = {}) => ({
  read: true,
  create: true,
  update: true,
  delete: true,
  scope,
  label,
  ...extras,
});

const cruNoDelete = (scope, label, extras = {}) => ({
  read: true,
  create: true,
  update: true,
  delete: false,
  scope,
  label,
  ...extras,
});

/**
 * Permission matrix: role → module → permission record.
 *
 * Each record: { read, create, update, delete, scope, partial?, sendQuote?, generateInvoice? }
 * - `partial: true` means "Partial Read (visible fields TBD)".
 * - `sendQuote` / `generateInvoice` are module sub-actions.
 */
export const PERMISSIONS = {
  super_admin: {
    booking: full(SCOPES.GLOBAL, 'Full Access (CRUD)', { sendQuote: true }),
    tracking: full(SCOPES.GLOBAL, 'Full Access (CRUD)'),
    clients: full(SCOPES.GLOBAL, 'Full Access (CRUD)'),
    financials: full(SCOPES.GLOBAL, 'Full Access (CRUD)', { generateInvoice: true }),
    admin: full(SCOPES.GLOBAL, 'Full Access'),
  },
  admin: {
    booking: full(SCOPES.GLOBAL, 'Full Access (CRUD)', { sendQuote: true }),
    tracking: full(SCOPES.GLOBAL, 'Full Access (CRUD)'),
    clients: full(SCOPES.GLOBAL, 'Full Access (CRUD)'),
    financials: full(SCOPES.GLOBAL, 'Full Access (CRUD)', { generateInvoice: true }),
    admin: full(SCOPES.GLOBAL, 'Full Access'),
  },
  broker: {
    booking: crud(SCOPES.SELF, 'CRUD (Self only)', { sendQuote: true }),
    tracking: crud(SCOPES.SELF, 'CRUD (Self only)'),
    clients: crud(SCOPES.SELF, 'CRUD (Self only)'),
    financials: readOnly(SCOPES.SELF, 'Read-Only + Generate Client Invoice', { generateInvoice: true }),
    admin: deny,
  },
  junior_broker: {
    booking: cruNoDelete(SCOPES.SELF, 'Draft (Self only)', { sendQuote: false }),
    tracking: cruNoDelete(SCOPES.SELF, 'CRU (Self only) No delete'),
    clients: cruNoDelete(SCOPES.SELF, 'CRU (Self only) No delete'),
    financials: readOnly(SCOPES.SELF, 'Read-Only (Self only)'),
    admin: deny,
  },
  flight_coordinator: {
    booking: readOnly(SCOPES.GLOBAL, 'Partial Read (Global)', { partial: true }),
    tracking: full(SCOPES.GLOBAL, 'CRUD (Global)'),
    clients: readOnly(SCOPES.GLOBAL, 'Partial Read (Global)', { partial: true }),
    financials: deny,
    admin: deny,
  },
  finance: {
    booking: readOnly(SCOPES.GLOBAL, 'Read-Only (Global)'),
    tracking: readOnly(SCOPES.GLOBAL, 'Read-Only (Global)'),
    clients: readOnly(SCOPES.GLOBAL, 'Partial Read (Global)', { partial: true }),
    financials: full(SCOPES.GLOBAL, 'Full Access (CRUD)'),
    admin: deny,
  },
  compliance_qa: {
    booking: readOnly(SCOPES.GLOBAL, 'Partial Read (Global)', { partial: true }),
    tracking: readOnly(SCOPES.GLOBAL, 'Read-Only (Global)'),
    clients: deny,
    financials: deny,
    admin: deny,
  },
};

/**
 * Ordered list of modules used by display surfaces (e.g. RolePermissionsModal).
 */
export const MODULE_DISPLAY_ORDER = [
  { key: MODULES.BOOKING, label: 'Booking & Quoting' },
  { key: MODULES.TRACKING, label: 'Flight Tracking' },
  { key: MODULES.CLIENTS, label: 'Client Catalog' },
  { key: MODULES.FINANCIALS, label: 'Financials' },
  { key: MODULES.ADMIN, label: 'Admin & Security' },
];

/**
 * Ordered list of roles used by display surfaces. Excludes `super_admin`
 * because that role is not a configurable team-member designation.
 */
export const ROLE_DISPLAY_ORDER = [
  { key: 'admin', label: 'Administrator' },
  { key: 'broker', label: 'Broker' },
  { key: 'junior_broker', label: 'Junior Broker' },
  { key: 'flight_coordinator', label: 'Flight Coordinator' },
  { key: 'finance', label: 'Finance / Accounting' },
  { key: 'compliance_qa', label: 'Compliance / QA' },
];

const DENY_RECORD = Object.freeze({ ...deny });

/**
 * Map DB-side role variants to the canonical matrix keys.
 * Extend here when new role spellings appear in `user_profiles.role`.
 */
const ROLE_ALIASES = {
  administrator: 'admin',
  ADMIN: 'admin',
  compliance: 'compliance_qa',
  'compliance/qa': 'compliance_qa',
  finance_accounting: 'finance',
  'finance/accounting': 'finance',
};

export function normalizeRole(role) {
  if (!role) return null;
  if (PERMISSIONS[role]) return role;
  if (ROLE_ALIASES[role]) return ROLE_ALIASES[role];
  const lower = String(role).toLowerCase();
  if (PERMISSIONS[lower]) return lower;
  if (ROLE_ALIASES[lower]) return ROLE_ALIASES[lower];
  return lower;
}

/**
 * Look up a permission record. Returns a deny-all record if the role or module
 * is unknown — fail-closed by default.
 */
export function getPermission(role, module) {
  if (!role || !module) return DENY_RECORD;
  const roleMap = PERMISSIONS[normalizeRole(role)];
  if (!roleMap) return DENY_RECORD;
  return roleMap[module] || DENY_RECORD;
}

/**
 * Check whether the role can perform a CRUD or sub-action on a module.
 * Unknown actions return false.
 */
export function canPerform(role, module, action) {
  const perm = getPermission(role, module);
  if (action === ACTIONS.READ) return !!perm.read;
  if (action === ACTIONS.CREATE) return !!perm.create;
  if (action === ACTIONS.UPDATE) return !!perm.update;
  if (action === ACTIONS.DELETE) return !!perm.delete;
  if (action === ACTIONS.SEND_QUOTE) return !!perm.sendQuote;
  if (action === ACTIONS.GENERATE_INVOICE) return !!perm.generateInvoice;
  return false;
}

/**
 * Any-read shortcut used by navigation and route gating.
 */
export function canAccessModule(role, module) {
  return !!getPermission(role, module).read;
}

export function getScope(role, module) {
  return getPermission(role, module).scope || SCOPES.NONE;
}
